import React, { useEffect, useRef, useState } from "react";
import { Loader2, ImagePlus } from "lucide-react";
import { t } from "@/lib/i18n";

// Cámara con guía de encuadre para fotografiar documentos (papel firmado, recibos de diésel).
// Si el navegador no permite acceso a la cámara, cae a un <input type=file capture> nativo.
export default function CamaraDocumento({ onCapture, onCancel, titulo = t("Align the paper inside the frame") }) {  // Fix Q (2026-10-13): app del chofer en inglés
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const fileInputRef = useRef(null);
  const [listo, setListo] = useState(false);
  const [error, setError] = useState("");
  const [capturando, setCapturando] = useState(false);

  useEffect(() => {
    let activo = true;
    (async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: "environment" }, width: { ideal: 1920 }, height: { ideal: 1080 } },
          audio: false,
        });
        if (!activo) { stream.getTracks().forEach((tt) => tt.stop()); return; }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play();
        }
        setListo(true);
      } catch {
        setError(t("Could not access the camera. Choose a photo from your phone."));
      }
    })();
    return () => {
      activo = false;
      streamRef.current?.getTracks().forEach((tt) => tt.stop());
    };
  }, []);

  const capturar = async () => {
    if (!videoRef.current) return;
    setCapturando(true);
    try {
      const video = videoRef.current;
      const canvas = document.createElement("canvas");
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      const blob = await new Promise((res) => canvas.toBlob(res, "image/jpeg", 0.92));
      if (!blob) return;
      onCapture(new File([blob], "documento.jpg", { type: "image/jpeg" }));
    } finally {
      setCapturando(false);
    }
  };

  const onSeleccionarArchivo = (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (f) onCapture(f);
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black flex flex-col" data-testid="camara-documento">
      <div className="relative flex-1 overflow-hidden bg-black">
        {!error ? (
          <video ref={videoRef} playsInline muted className="w-full h-full object-cover" />
        ) : (
          <div className="h-full flex items-center justify-center p-6 text-center text-[#D3CDC2] text-sm" data-testid="camara-error">{error}</div>
        )}
        <div className="absolute top-4 inset-x-0 flex justify-center px-4 pointer-events-none">
          <p className="rounded-full bg-black/70 text-white text-[14px] font-medium px-4 py-2">{titulo}</p>
        </div>
        {listo && !error && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none p-8 pt-16">
            <div className="relative w-full max-w-xs aspect-[3/4] max-h-full" style={{ boxShadow: "0 0 0 9999px rgba(0,0,0,0.55)" }} data-testid="camara-guia">
              <span className="absolute -top-0.5 -left-0.5 h-7 w-7 border-t-4 border-l-4 border-white rounded-tl-md" />
              <span className="absolute -top-0.5 -right-0.5 h-7 w-7 border-t-4 border-r-4 border-white rounded-tr-md" />
              <span className="absolute -bottom-0.5 -left-0.5 h-7 w-7 border-b-4 border-l-4 border-white rounded-bl-md" />
              <span className="absolute -bottom-0.5 -right-0.5 h-7 w-7 border-b-4 border-r-4 border-white rounded-br-md" />
            </div>
          </div>
        )}
      </div>

      <div className="px-6 py-5 grid grid-cols-3 items-center bg-black shrink-0" style={{ paddingBottom: "calc(1.25rem + env(safe-area-inset-bottom))" }}>
        <input ref={fileInputRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={onSeleccionarArchivo} />
        <button data-testid="camara-cerrar" onClick={onCancel} className="justify-self-start text-white text-[16px] font-medium py-3">{t("Cancel")}</button>
        <button
          type="button"
          data-testid="camara-capturar"
          disabled={!listo || !!error || capturando}
          onClick={capturar}
          className="justify-self-center h-[84px] w-[84px] rounded-full border-2 border-white flex items-center justify-center active:scale-95 disabled:opacity-40"
          aria-label={t("Take photo")}
        >
          <span className="h-[72px] w-[72px] rounded-full bg-white flex items-center justify-center">
            {capturando && <Loader2 className="h-6 w-6 animate-spin text-ink" />}
          </span>
        </button>
        <button type="button" data-testid="camara-elegir-archivo" onClick={() => fileInputRef.current?.click()} className="justify-self-end flex flex-col items-center gap-1 text-white text-[13px] py-1">
          <ImagePlus className="h-6 w-6" />{t("Choose photo")}</button>
      </div>
    </div>
  );
}
